import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import {
  AuditTrailForExport,
  getAuditTrailData,
} from "../util/getAuditTrailData";

import { uploadJsonToS3 } from "../util/AppUtils";

const baseUrl = BASE_URL + "parts";
const bomBaseUrl = BASE_URL + "part-req-bom";
const procedureBaseUrl = BASE_URL + "procedure-revision";

// 📌 Create Part (POST)
export const createPart = createAsyncThunk(
  "part/create",
  async (partData, { rejectWithValue }) => {
    try {
      const version_content = {
        ...partData,
        action_to_show: `Created Item Code ${partData.part_number}`,
      }

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "item_code_created",
        "CREATE",
        true
      );

      const ad = await getAuditTrailData({
        action: "CREATE_PART",
        comment: `Created Item Code ${partData.part_number}`,
        version_content: {
          action_to_show: version_content.action_to_show,
        },
        new_content_snapshot,
      });

      const payload = {
        ...partData,
        URL: partData.url,
        audit_trail_data: ad,
      };

      const response = await axios.post(`${baseUrl}/create`, payload);

      return response.data;
    } catch (error) {
      console.error("Error creating part:", error);
      return rejectWithValue(error.response?.data || "Error creating part");
    }
  }
);

// 📌 Update Part (PUT)
export const updatePart = createAsyncThunk(
  "part/update",
  async ({ id, data, parent_version }, { rejectWithValue }) => {
    try {
      const version_content = { ...data, action_to_show: "Edit Items Pop-Up Table" }

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "item_code_updated",
        "UPDATE",
        true
      );

      const ad = await getAuditTrailData({
        action: "UPDATE_PART",
        comment: `Update Item Code ${data.part_number}`,
        version_content: { action_to_show: version_content.action_to_show },
        parent_version: parent_version,
        new_content_snapshot,
      });

      const payload = {
        ...data,
        URL: data.url,
        audit_trail_data: ad,
      };

      const response = await axios.put(`${baseUrl}/update/${id}`, payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating part");
    }
  }
);

// 📌 Get Part by ID (GET)
export const getPartById = createAsyncThunk(
  "part/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching part");
    }
  }
);

// 📌 Get All Parts (GET)
export const getAllParts = createAsyncThunk(
  "part/getAll",
  async (
    { limit = 10, offset = 0, partNumber = "", type = "" },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}&part_number=${partNumber}&type=${type}&check_module_created=true`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getAllProcedureParts = createAsyncThunk(
  "part/getAllProcedureParts",
  async (
    { limit, offset, partNumber = "", type = "" },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}&is_procedure=true&part_number=${partNumber}&type=${type}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getAllProcedures = createAsyncThunk(
  "part/getAllProcedures",
  async (
    {
      limit,
      offset,
      search = "",
      search_type = "",
      sort_by = "",
      sort_order = "",
      is_hide_current = false,
      procedure_revision_id = "",
    },
    { rejectWithValue }
  ) => {
    try {
      let queryParams = `status=Not Submitted,Change Requested&limit=${limit}&offset=${offset}`;

      if (search) {
        queryParams += `&search=${encodeURIComponent(search)}`;
      }

      if (search_type) {
        queryParams += `&search_type=${encodeURIComponent(search_type)}`;
      }

      if (sort_by) {
        queryParams += `&sort_by=${encodeURIComponent(sort_by)}`;
      }

      if (sort_order) {
        queryParams += `&sort_order=${encodeURIComponent(sort_order)}`;
      }

      if (is_hide_current) {
        queryParams += `&is_hide_current=true`;
      }

      if (procedure_revision_id) {
        queryParams += `&procedure_revision_id=${encodeURIComponent(procedure_revision_id)}`;
      }

      const response = await axios.get(
        `${procedureBaseUrl}/get-all?${queryParams}&check_module_created=true`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getFilterdProcedureParts = createAsyncThunk(
  "part/getFilterdProcedureParts",
  async ({ query }, { rejectWithValue }) => {
    const qr = query ? query : "";
    try {
      const response = await axios.get(
        `${procedureBaseUrl}/get-part-procedure?limit=1000&offset=0&search=${qr}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getLastSibling = createAsyncThunk(
  "part/getLastSibling",
  async (
    {
      partId = "",
      type = "",
      revisionId = "",
      sectionId = "",
      stepId = "",
      substepId = "",
      childId = "",
    },
    { rejectWithValue }
  ) => {
    try {
      let finalSubstepId = substepId;
      if (substepId === "undefined" || substepId === "null") {
        finalSubstepId = "";
      }
      const response = await axios.post(
        `${procedureBaseUrl}/get-last-sibling`,
        {
          type,
          part_id_ref: partId,
          procedure_rev_ref: revisionId,
          section_id_ref: sectionId,
          step_id_ref: stepId,
          substep_id_ref: finalSubstepId,
          child_node_ref: childId,
        }
      );
      return response.data?.results?.total_count || 0;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getSibling = createAsyncThunk(
  "part/getSibling",
  async (
    {
      partId = "",
      type = "",
      revisionId = "",
      sectionId = "",
      stepId = "",
      substepId = "",
      childId = "",
    },
    { rejectWithValue }
  ) => {
    try {
      let finalSubstepId = substepId;
      if (substepId === "undefined" || substepId === "null") {
        finalSubstepId = "";
      }
      const response = await axios.post(
        `${procedureBaseUrl}/get-last-sibling`,
        {
          type,
          part_id_ref: partId,
          procedure_rev_ref: revisionId,
          section_id_ref: sectionId,
          step_id_ref: stepId,
          substep_id_ref: finalSubstepId,
          child_node_ref: childId,
        }
      );
      return response.data?.results?.sibling_order || 0;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getAllFlowchartParts = createAsyncThunk(
  "part/getAllFlowchartParts",
  async (
    { limit = 100, offset = 0, partNumber = "", type = "" },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}&is_flowchart=true&part_number=${partNumber}&type=${type}&check_module_created=true`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getAllModuleParts = createAsyncThunk(
  "part/getAllModuleParts",
  async (
    { limit = 1000, offset = 0, partNumber, type },
    { rejectWithValue }
  ) => {
    let pnumber = partNumber ? partNumber : "";
    let ptype = type ? type : "";
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}&is_module=true&search=${pnumber}&check_module_created=true`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

// Fetch All Parts
export const fetchAllParts = createAsyncThunk(
  "part/fetchAll",
  async ({ searchQuery }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-all?${searchQuery}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const fetchRecentParts = createAsyncThunk(
  "part/fetchRecent",
  async ({ limit, sort_by, sort_order, filterType }, { rejectWithValue }) => {
    try {
      let query = `limit=${limit}&offset=0&recent_item_sort=true`;
      if (sort_by !== null) {
        query = query + `&sort_by=${sort_by}&sort_order=${sort_order}`;
      }
      if (filterType !== null) {
        query = query + filterType;
      }
      const response = await axios.get(`${baseUrl}/get-all?${query}&check_module_created=true`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

export const getBom = createAsyncThunk(
  "part/getBom",
  async ({ partId, revisionId, withoutToken = false }, { rejectWithValue }) => {
    try {
      let endPoint = withoutToken ? `${bomBaseUrl}/get-bom` : `${bomBaseUrl}/get-all`;
      const response = await axios.get(
        `${endPoint}?limit=100000&part_ref=${partId}&procedure_rev_ref=${revisionId}&is_hide_BOM_table=false`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching parts");
    }
  }
);

// 📌 Delete Part (DELETE)
export const deletePart = createAsyncThunk(
  "part/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting part");
    }
  }
);

export const exportCSV = createAsyncThunk(
  "part/exportCSV",
  async (_, { rejectWithValue }) => {
    try {
      await AuditTrailForExport({
        apiUrl: `${baseUrl}/export-csv`,
        file_name: "export-parts-data-" + Date.now() + ".csv",
        action: "EXPORT_PARTS_DATA",
        action_to_show: "Item Code Table data exported as CSV",
      });
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error exporting CSV");
    }
  }
);

export const uploadCSV = createAsyncThunk(
  "part/uploadCSV",
  async ({ csvFile, auditTrailData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      formData.append("audit_trail_data", JSON.stringify(auditTrailData));

      const response = await axios.post(`${baseUrl}/upload-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error uploading CSV");
    }
  }
);

export const getPartMentions = createAsyncThunk(
  "part/getPartMentions",
  async (
    {
      partNumber,
      partId,
      limit = 20,
      offset = 0,
      search = "",
      searchType,
      sortBy,
      sortOrder,
      showModule = false,
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();

      if (partNumber) {
        params.append("part_number", partNumber);
      }
      params.append("limit", limit);
      params.append("offset", offset);

      if (search) {
        params.append("search", search);
      }
      if (searchType) {
        params.append("search_type", searchType);
      }
      if (sortBy) {
        params.append("sort_by", sortBy);
      }
      if (sortOrder) {
        params.append("sort_order", sortOrder);
      }

      if (partId !== undefined && partId !== null && partId !== "") {
        params.append("part_ref", partId);
      }

      // Use show_module=true for modules, show_procedure=true for procedures
      const showParam = showModule ? "show_module=true" : "show_procedure=true";

      const response = await axios.get(
        `${baseUrl}/part-mentions?${params.toString()}&${showParam}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching part mentions"
      );
    }
  }
);

// 🛠 Part Slice
const partSlice = createSlice({
  name: "part",
  initialState: {
    parts: [],
    procedureParts: [],
    flowchartParts: [],
    moduleParts: [],
    bomTable: [],
    part: null,
    loading: false,
    error: null,
    uploadError: null,
    fileterdParts: [],
    procedures: [],

    disabledFilters: [],
    procedure_rev_ref_id: null,
    part_ref_id: null,
    step_tree_node_id: null,
    partMentions: [],
    partMentionsPagination: null,
    partMentionsLoading: false,
    partMentionsError: null,
    partMentionsIsModule: false,
    // Separate state for modules mentions
    modulesMentions: [],
    modulesMentionsPagination: null,
    modulesMentionsLoading: false,
    modulesMentionsError: null,
  },
  reducers: {
    disableFilters: (state, action) => {
      state.disabledFilters = action.payload;
    },
    setProcRevId: (state, action) => {
      state.procedure_rev_ref_id = action.payload.revid;
      state.part_ref_id = action.payload.part_ref_id;
    },
    setStepTreeNodeId: (state, action) => {
      state.step_tree_node_id = action.payload;
    },
    resetParts: (state) => {
      state.parts = [];
      state.procedureParts = [];
      state.flowchartParts = [];
      state.moduleParts = [];
      state.bomTable = [];
      state.part = null;
      state.loading = false;
      state.error = null;
      state.uploadError = null;
      state.fileterdParts = [];
      state.procedures = [];
      state.disabledFilters = [];
      state.procedure_rev_ref_id = null;
      state.part_ref_id = null;
      state.step_tree_node_id = null;
      state.partMentions = [];
      state.partMentionsPagination = null;
      state.partMentionsLoading = false;
      state.partMentionsError = null;
      state.modulesMentions = [];
      state.modulesMentionsPagination = null;
      state.modulesMentionsLoading = false;
      state.modulesMentionsError = null;
    },
    clearPartMentions: (state) => {
      state.partMentions = [];
      state.partMentionsPagination = null;
      state.partMentionsLoading = false;
      state.partMentionsError = null;
      state.partMentionsIsModule = false;
      state.modulesMentions = [];
      state.modulesMentionsPagination = null;
      state.modulesMentionsLoading = false;
      state.modulesMentionsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPart.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPart.fulfilled, (state, action) => {
        state.loading = false;
        // state.parts.push(action.payload);
      })
      .addCase(createPart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(updatePart.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePart.fulfilled, (state, action) => {
        state.loading = false;
        state.parts = state.parts.map((part) =>
          part.id === action.payload.id ? action.payload : part
        );
      })
      .addCase(updatePart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getPartById.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPartById.fulfilled, (state, action) => {
        state.loading = false;
        state.part = action.payload;
      })
      .addCase(getPartById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getAllParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllParts.fulfilled, (state, action) => {
        state.loading = false;
        state.parts = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getAllProcedures.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProcedures.fulfilled, (state, action) => {
        state.loading = false;
        state.procedures = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllProcedures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(exportCSV.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportCSV.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportCSV.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      // Upload CSV
      .addCase(uploadCSV.pending, (state) => {
        state.loading = true;
        state.uploadError = null;
      })
      .addCase(uploadCSV.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadError = null;
        const payload = action.payload;

        // Check if failed data exists
        const failedCount = payload?.failed?.length || 0;
        state.uploadError =
          failedCount > 0
            ? `Error while importing: ${failedCount} row(s) failed`
            : payload.message || payload.error || "An error occurred";
      })
      .addCase(uploadCSV.rejected, (state, action) => {
        state.loading = false;

        const payload = action.payload;

        // Check if failed data exists
        const failedCount = payload?.failed?.length || 0;

        // Compose a message with count if failures are present
        state.uploadError =
          failedCount > 0
            ? `${
                payload.message || "Upload failed"
              } — ${failedCount} row(s) failed testing`
            : payload.message || payload.error || "An error occurred";
      })
      // Bom Table
      .addCase(getBom.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBom.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.bomTable = action.payload.results;
      })
      .addCase(getBom.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload.message || action.payload.error || "An error occured";
      })

      .addCase(getAllProcedureParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProcedureParts.fulfilled, (state, action) => {
        state.loading = false;
        state.procedureParts = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllProcedureParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getFilterdProcedureParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFilterdProcedureParts.fulfilled, (state, action) => {
        state.loading = false;
        state.fileterdParts = action.payload.results;
        state.error = null;
      })
      .addCase(getFilterdProcedureParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(getAllFlowchartParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllFlowchartParts.fulfilled, (state, action) => {
        state.loading = false;
        state.flowchartParts = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllFlowchartParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getAllModuleParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllModuleParts.fulfilled, (state, action) => {
        state.loading = false;
        state.moduleParts = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(getAllModuleParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "An error occurred";
      })
      .addCase(fetchAllParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllParts.fulfilled, (state, action) => {
        state.loading = false;
        state.parts = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchAllParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })

      .addCase(fetchRecentParts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentParts.fulfilled, (state, action) => {
        state.loading = false;
        state.parts = action.payload.results;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchRecentParts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(deletePart.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePart.fulfilled, (state, action) => {
        state.loading = false;
        state.parts = state.parts.filter((part) => part.id !== action.payload);
      })
      .addCase(deletePart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || "An error occurred";
      })
      .addCase(getPartMentions.pending, (state, action) => {
        // Check if this is a modules call by checking the meta.arg
        const isModuleCall = action.meta?.arg?.showModule === true;
        
        if (isModuleCall) {
          state.modulesMentionsLoading = true;
          state.modulesMentionsError = null;
        } else {
          state.partMentionsLoading = true;
          state.partMentionsError = null;
        }
      })
      .addCase(getPartMentions.fulfilled, (state, action) => {
        // Handle nested results structure: payload.results.rows
        const resultsData = action.payload?.results;
        
        // Use the action meta arg to determine if it's a modules call
        // The API response's is_module field may not be reliable
        const isModuleCall = action.meta?.arg?.showModule === true;
        
        const data =
          resultsData?.rows ??
          (Array.isArray(resultsData) ? resultsData : []) ??
          action.payload?.data ??
          [];
        const pagination =
          action.payload?.pagination ?? action.payload?.meta ?? null;
        
        if (isModuleCall) {
          // Store in modules mentions state
          state.modulesMentions = data;
          state.modulesMentionsPagination = pagination;
          state.modulesMentionsLoading = false;
          state.modulesMentionsError = null;
        } else {
          // Store in procedures mentions state
          state.partMentions = data;
          state.partMentionsPagination = pagination;
          state.partMentionsLoading = false;
          state.partMentionsError = null;
          state.partMentionsIsModule = resultsData?.is_module === true;
        }
      })
      .addCase(getPartMentions.rejected, (state, action) => {
        const error =
          action.payload?.message ||
          action.payload?.error ||
          (typeof action.payload === "string"
            ? action.payload
            : "An error occurred");
        
        // Check if this is a modules call by checking the meta.arg
        const isModuleCall = action.meta?.arg?.showModule === true;
        
        if (isModuleCall) {
          state.modulesMentionsLoading = false;
          state.modulesMentionsError = error;
        } else {
          state.partMentionsLoading = false;
          state.partMentionsError = error;
        }
      });
  },
});

export const {
  disableFilters,
  setProcRevId,
  setStepTreeNodeId,
  resetParts,
  clearPartMentions,
} = partSlice.actions;
export default partSlice.reducer;
